// src/pages/Home.jsx — FINAL FIXED
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container, Typography, Button, Grid, Card, CardContent,
  CardActions, Box, TextField, InputAdornment, Paper, Chip,
  IconButton, useMediaQuery, useTheme, alpha, Stack, Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Search, LocalShipping, SupportAgent, ArrowRightAlt, Public,
  TrendingUp, Security, RocketLaunch, PlayArrow, Star,
  Fingerprint, ShoppingCart, Handshake, AccessTime, GppGood,
  AssignmentTurnedIn,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { fruitsAPI } from '../services/api';

const _BACKEND = (process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:5000').replace(/\/$/, '');

// ✅ FINAL image resolver — always uses image_path (raw filename) to build correct URL
const getImageSrc = (fruit) => {
  if (!fruit) return null;
  const rawPath = fruit.image_path || fruit.image_url;
  if (!rawPath) return null;
  if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) return rawPath;
  const filename = rawPath.split('/').pop().split('\\').pop();
  if (!filename) return null;
  return `${_BACKEND}/static/uploads/fruits/${filename}`;
};

const getCategoryEmoji = (category) => {
  const map = {
    Apple: '🍎', Pineapple: '🍍', Mango: '🥭',
    Orange: '🍊', Grapes: '🍇', Banana: '🍌',
    Berries: '🫐', Melon: '🍈', Citrus: '🍋',
    Tropical: '🥥', Exotic: '🥝',
  };
  return map[category] || '🍎';
};

const getPrice = (f) => parseFloat(f?.price_per_unit || 0);

// ─── Fruit Card ───────────────────────────────────────────────────────────────
const FruitCard = ({ fruit }) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const imgSrc = getImageSrc(fruit);
  const price  = getPrice(fruit);

  return (
    <Card sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      borderRadius: 3, overflow: 'hidden',
      border: '1px solid', borderColor: 'divider',
      transition: 'all 0.3s',
      '&:hover': {
        boxShadow: '0 16px 48px rgba(0,0,0,0.14)',
        transform: 'translateY(-4px)',
        '& .fc-img': { transform: 'scale(1.06)' },
      },
    }} elevation={0}>

      {/* Image */}
      <Box sx={{ position: 'relative', overflow: 'hidden', height: 200, bgcolor: 'grey.100' }}>
        {imgSrc && !imgError ? (
          <Box
            className="fc-img"
            component="img"
            src={imgSrc}
            alt={fruit.name}
            onError={() => setImgError(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }}
          />
        ) : (
          <Box sx={{
            width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)',
            fontSize: '4rem',
          }}>
            {getCategoryEmoji(fruit.category)}
          </Box>
        )}
        {Array.isArray(fruit.origins) && fruit.origins[0] && (
          <Chip
            label={fruit.origins[0]} size="small"
            icon={<Public sx={{ fontSize: '12px !important' }} />}
            sx={{
              position: 'absolute', bottom: 8, left: 8,
              bgcolor: alpha('#000', 0.65), color: 'white',
              backdropFilter: 'blur(4px)',
              '& .MuiChip-icon': { color: 'white' },
            }}
          />
        )}
      </Box>

      {/* Content */}
      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ maxWidth: '65%', lineHeight: 1.2 }}>
            {fruit.name}
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            {price > 0 ? (
              <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                ${price.toFixed(2)}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary" fontStyle="italic">
                On request
              </Typography>
            )}
          </Box>
        </Box>

        {fruit.category && (
          <Chip label={fruit.category} size="small" variant="outlined" color="primary"
            sx={{ mb: 1, height: 20, fontSize: '0.7rem' }} />
        )}

        {Array.isArray(fruit.varieties) && fruit.varieties.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {fruit.varieties.slice(0, 2).map((v, i) => (
              <Chip key={i} label={v} size="small"
                sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'grey.100' }} />
            ))}
            {fruit.varieties.length > 2 && (
              <Chip label={`+${fruit.varieties.length - 2}`} size="small"
                sx={{ height: 18, fontSize: '0.65rem' }} />
            )}
          </Box>
        )}

        {Array.isArray(fruit.grades) && fruit.grades.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Star sx={{ fontSize: 14, color: '#ff9800' }} />
            <Typography variant="caption" color="text.secondary">
              {fruit.grades.join(' · ')}
            </Typography>
          </Box>
        )}

        {fruit.moq && (
          <Typography variant="caption" color="text.secondary" display="block">
            MOQ: {fruit.moq}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button fullWidth variant="contained" endIcon={<ArrowRightAlt />}
          onClick={() => navigate(`/fruits/${fruit.id}`)} sx={{ borderRadius: 2 }}>
          View Details
        </Button>
      </CardActions>
    </Card>
  );
};

// ─── Home ─────────────────────────────────────────────────────────────────────
const Home = () => {
  const [searchTerm, setSearchTerm]   = useState('');
  const [fruits, setFruits]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [typingIndex, setTypingIndex] = useState(0);
  const [typingText, setTypingText]   = useState('');
  const [isDeleting, setIsDeleting]   = useState(false);

  // Detect logged-in user role to show activity badge
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();
  const userActivity = currentUser?.user_type === 'seller' ? 'Exporting'
                     : currentUser?.user_type === 'buyer'  ? 'Importing'
                     : currentUser?.user_type === 'admin'  ? 'Managing'
                     : null;

  const navigate    = useNavigate();
  const theme       = useTheme();
  const isMobile    = useMediaQuery(theme.breakpoints.down('sm'));
  const featuresRef = useRef(null);
  const stepsRef    = useRef(null);

  const typingWords = [
    'Global Fruit Markets',
    'Verified Suppliers',
    'Fresh Produce Network',
    'Secure Trade Platform',
  ];

  const fetchFruits = useCallback(async () => {
    try {
      setLoading(true);
      let response;
      try { response = await fruitsAPI.getFeatured(); }
      catch { response = await fruitsAPI.getAll(); }
      const raw = response?.data?.fruits || response?.data || [];
      setFruits(Array.isArray(raw) ? raw.slice(0, 8) : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load fruits');
      setFruits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFruits(); }, [fetchFruits]);

  // Typing animation
  useEffect(() => {
    let timeout;
    const type = () => {
      const word = typingWords[typingIndex];
      if (!isDeleting && typingText === word) {
        timeout = setTimeout(() => setIsDeleting(true), 1800); return;
      }
      if (isDeleting && typingText === '') {
        setIsDeleting(false);
        setTypingIndex((p) => (p + 1) % typingWords.length); return;
      }
      const next = isDeleting
        ? word.substring(0, typingText.length - 1)
        : word.substring(0, typingText.length + 1);
      setTypingText(next);
      timeout = setTimeout(type, isDeleting ? 45 : 95);
    };
    timeout = setTimeout(type, 400);
    return () => clearTimeout(timeout);
  }, [typingText, isDeleting, typingIndex]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) navigate(`/fruits?search=${encodeURIComponent(searchTerm)}`);
  };

  const scrollToFeatures = useCallback(() => featuresRef.current?.scrollIntoView({ behavior: 'smooth' }), []);
  const scrollToSteps    = useCallback(() => stepsRef.current?.scrollIntoView({ behavior: 'smooth' }), []);

  const features = [
    { icon: <LocalShipping sx={{ fontSize: 40 }} />,     title: 'Global Shipping',    description: 'Direct shipments from origin to destination ports', color: '#2e7d32', stats: '150+ Countries' },
    { icon: <Star sx={{ fontSize: 40 }} />,              title: 'Verified Partners',   description: 'Trade only with verified and trusted businesses',    color: '#1976d2', stats: '500+ Suppliers' },
    { icon: <GppGood sx={{ fontSize: 40 }} />,           title: 'Quality Inspection', description: 'Rigorous quality checks and certification',          color: '#ff9800', stats: 'Grade A Standards' },
    { icon: <AssignmentTurnedIn sx={{ fontSize: 40 }} />,title: 'Customs Clearance',  description: 'End-to-end customs documentation support',           color: '#9c27b0', stats: '24h Processing' },
    { icon: <Security sx={{ fontSize: 40 }} />,          title: 'Secure Payments',    description: 'Protected payment workflow with escrow system',      color: '#ed6c02', stats: '$2B+ Processed' },
    { icon: <SupportAgent sx={{ fontSize: 40 }} />,      title: '24/7 Support',       description: 'Round-the-clock support via chat and WhatsApp',      color: '#00acc1', stats: '10 Min Response' },
  ];

  const tradingSteps = [
    { number: '01', title: 'Register & Verify',   description: 'Create your account and complete business verification',   color: '#4caf50', duration: '2 mins' },
    { number: '02', title: 'Browse Marketplace',  description: 'Explore thousands of quality fruits from verified growers', color: '#2196f3', duration: 'Instant' },
    { number: '03', title: 'Connect & Negotiate', description: 'Direct communication with suppliers for best deals',       color: '#ff9800', duration: '24h response' },
    { number: '04', title: 'Quality Inspection',  description: 'Professional quality checks and certification',           color: '#ff5722', duration: 'Certified' },
    { number: '05', title: 'Secure Payment',      description: 'Protected payment process with escrow service',           color: '#9c27b0', duration: 'Secure escrow' },
    { number: '06', title: 'Customs & Shipping',  description: 'Documentation, customs clearance and real-time tracking', color: '#00bcd4', duration: 'Live updates' },
  ];

  const trendingCategories = [
    { name: 'Tropical Fruits', count: '120+', color: '#ff9800', icon: '🥭' },
    { name: 'Citrus',          count: '85+',  color: '#ffc107', icon: '🍊' },
    { name: 'Berries',         count: '65+',  color: '#e91e63', icon: '🫐' },
    { name: 'Melons',          count: '45+',  color: '#4caf50', icon: '🍈' },
    { name: 'Stone Fruits',    count: '75+',  color: '#9c27b0', icon: '🍑' },
    { name: 'Exotics',         count: '50+',  color: '#2196f3', icon: '🥝' },
  ];

  const stats = [
    { value: '500+', label: 'Verified Suppliers', icon: '🏭' },
    { value: '150+', label: 'Countries',           icon: '🌎' },
    { value: '24h',  label: 'Avg. Response Time',  icon: '⚡' },
    { value: '$2B+', label: 'Annual Trade',         icon: '💰' },
    { value: '99%',  label: 'Quality Pass Rate',    icon: '⭐' },
    { value: '48h',  label: 'Customs Clearance',    icon: '📄' },
  ];

  const testimonials = [
    { name: 'Alex Chen',       role: 'Import Manager, FreshMart',         content: 'Reduced sourcing time by 70% and improved supplier quality.',       rating: 5, avatar: 'AC' },
    { name: 'Maria Rodriguez', role: 'CEO, Tropical Fruits Inc.',          content: 'The platform helped us expand to 3 new countries in 6 months.',     rating: 5, avatar: 'MR' },
    { name: 'James Wilson',    role: 'Procurement Director, Global Foods', content: 'Secure payments and real-time tracking transformed our operations.', rating: 5, avatar: 'JW' },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>

      {/* ══════════════════════ HERO ══════════════════════ */}
      <Box sx={{
        position: 'relative',
        background: 'linear-gradient(135deg, #0d2f12 0%, #1b5e20 30%, #2e7d32 100%)',
        color: 'white',
        pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 15 },
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex', alignItems: 'center', overflow: 'hidden',
      }}>
        {[400, 300, 200].map((s, i) => (
          <Box key={i} sx={{
            position: 'absolute', borderRadius: '50%',
            bgcolor: alpha('#4caf50', 0.06), width: s, height: s,
            top: ['-5%','40%','70%'][i], right: ['5%','-5%','15%'][i],
            pointerEvents: 'none',
          }} />
        ))}

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">

            {/* Left */}
            <Grid item xs={12} md={6}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                  {userActivity && (
                    <Chip
                      label={userActivity}
                      sx={{
                        bgcolor: userActivity === 'Exporting' ? '#ff9800'
                               : userActivity === 'Importing' ? '#2196f3'
                               : '#4caf50',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.85rem',
                        px: 1,
                        '& .MuiChip-label': { px: 1.5 },
                      }}
                      icon={userActivity === 'Exporting'
                        ? <RocketLaunch sx={{ color: 'white !important' }} />
                        : <LocalShipping sx={{ color: 'white !important' }} />}
                    />
                  )}
                  <Chip label="TRUSTED GLOBAL PLATFORM" icon={<Public />}
                    sx={{ bgcolor: alpha('#ffeb3b', 0.2), color: '#ffeb3b', fontWeight: 600, '& .MuiChip-icon': { color: '#ffeb3b' } }} />
                  <Chip label="5,000+ TRADERS"
                    sx={{ bgcolor: alpha('#fff', 0.1), color: 'white', fontWeight: 600 }} />
                </Stack>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
                <Typography fontWeight={800} sx={{
                  lineHeight: 1.1, mb: 2,
                  fontSize: { xs: '1.8rem', sm: '2.4rem', md: '3rem', lg: '3.5rem' },
                }}>
                  Direct Access to
                </Typography>
                <Box sx={{
                  minHeight: { xs: '2.4rem', sm: '3rem', md: '3.8rem' },
                  mb: 2,
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                }}>
                  <Box component="span" sx={{
                    background: 'linear-gradient(90deg,#ffeb3b 0%,#ff9800 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    fontWeight: 800,
                    fontSize: { xs: '1.8rem', sm: '2.4rem', md: '3rem', lg: '3.5rem' },
                    lineHeight: 1.2, wordBreak: 'break-word',
                  }}>
                    {typingText}
                  </Box>
                  <Box component="span" sx={{
                    display: 'inline-block', width: '3px',
                    height: { xs: '1.6rem', sm: '2rem', md: '2.4rem' },
                    bgcolor: '#ff9800', ml: '2px', flexShrink: 0, verticalAlign: 'middle',
                    animation: 'blink 1s step-end infinite',
                    '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } },
                  }} />
                </Box>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
                <Typography variant="h6" sx={{
                  mb: 4, opacity: 0.9, fontWeight: 400, maxWidth: '90%',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}>
                  Connect with verified fruit exporters worldwide. Secure transactions,
                  real-time tracking, and premium quality assurance for your business.
                </Typography>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
                <Paper component="form" onSubmit={handleSearch} sx={{
                  p: { xs: 1.5, sm: 2 }, mb: 4,
                  bgcolor: alpha('#fff', 0.08), backdropFilter: 'blur(20px)',
                  borderRadius: 4, border: '1px solid rgba(255,255,255,0.15)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth placeholder="Search fruits, varieties, origins..."
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { border: 'none' } } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: alpha('#fff', 0.7) }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button type="submit" variant="contained" size="large" sx={{
                      background: 'linear-gradient(90deg,#ffeb3b,#ff9800)',
                      color: '#1b5e20', fontWeight: 'bold', borderRadius: 3, px: 4, whiteSpace: 'nowrap',
                    }}>
                      Search
                    </Button>
                  </Box>
                </Paper>
              </motion.div>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
                  <Button onClick={scrollToSteps} variant="contained" size="large" endIcon={<ArrowRightAlt />}
                    sx={{ background: 'linear-gradient(90deg,#4caf50,#2e7d32)', color: 'white', fontWeight: 'bold', borderRadius: 3, px: 4 }}>
                    Start Trading
                  </Button>
                </motion.div>
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.5 }}>
                  <Button onClick={scrollToFeatures} variant="outlined" size="large" startIcon={<RocketLaunch />}
                    sx={{ borderColor: alpha('#fff', 0.3), color: 'white', borderRadius: 3, px: 4, backdropFilter: 'blur(10px)' }}>
                    Explore Features
                  </Button>
                </motion.div>
              </Stack>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6 }}>
                <Grid container spacing={2}>
                  {stats.map((s, i) => (
                    <Grid item xs={6} sm={4} key={i}>
                      <Box>
                        <Typography variant="h5" fontWeight="bold">{s.value}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box component="span" sx={{ fontSize: '1rem' }}>{s.icon}</Box> {s.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>

            {/* Right — floating cards */}
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
                <Box sx={{ position: 'relative', height: 480 }}>
                  <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%,-50%)',
                    width: 340, height: 340,
                    borderRadius: '40% 60% 60% 40% / 50% 50% 70% 30%',
                    background: 'linear-gradient(135deg,rgba(76,175,80,0.25),rgba(27,94,32,0.4))',
                    border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '6rem',
                  }}>🍎</Box>

                  {[
                    { emoji: '🍊', label: 'Fresh Oranges',   sub: 'From Spain',     top: '8%',  left: '0%',  bg: '#ff9800' },
                    { emoji: '🥭', label: 'Premium Mangoes', sub: 'From India',     top: '68%', left: '0%',  bg: '#4caf50' },
                    { emoji: '🍇', label: 'Quality Grapes',  sub: 'From Italy',     top: '8%',  right: '0%', bg: '#9c27b0' },
                    { emoji: '🥝', label: 'Exotic Kiwi',     sub: 'From N.Zealand', top: '68%', right: '0%', bg: '#2196f3' },
                  ].map((item, i) => (
                    <Paper key={i} sx={{
                      position: 'absolute',
                      top: item.top, left: item.left, right: item.right,
                      p: 1.5, borderRadius: 3,
                      bgcolor: alpha('#fff', 0.1), backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      width: 170, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                      overflow: 'hidden',
                    }}>
                      <Avatar sx={{ bgcolor: item.bg, width: 40, height: 40, fontSize: '1.4rem' }}>
                        {item.emoji}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold" color="white" noWrap>{item.label}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8, color: 'white' }}>{item.sub}</Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════ FEATURES / WHY CHOOSE US ══════════════════════ */}
      <Container ref={featuresRef} maxWidth="xl" sx={{ py: { xs: 6, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
          <Chip label="WHY CHOOSE US" sx={{ bgcolor: alpha('#4caf50', 0.1), color: '#4caf50', fontWeight: 600, mb: 2 }} />
          <Typography variant="h3" fontWeight="bold" gutterBottom>Trade Smarter, Grow Faster</Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Everything you need to streamline your fruit trading operations
          </Typography>
        </Box>
        {/* Mobile: horizontal scroll | Desktop: grid */}
        <Box sx={{
          display: { xs: 'flex', md: 'grid' },
          gridTemplateColumns: { md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          gap: 3,
          overflowX: { xs: 'auto', md: 'unset' },
          pb: { xs: 2, md: 0 },
          scrollSnapType: { xs: 'x mandatory', md: 'none' },
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#4caf50', 0.3), borderRadius: 2 },
        }}>
          {features.map((f, i) => (
            <Box key={i} sx={{ minWidth: { xs: 200, md: 'unset' }, scrollSnapAlign: 'start', flexShrink: 0 }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                <Card sx={{
                  height: '100%', textAlign: 'center', p: 3, borderRadius: 4,
                  border: `2px solid ${alpha(f.color, 0.1)}`, transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-8px)', boxShadow: `0 20px 40px ${alpha(f.color, 0.15)}` },
                }} elevation={0}>
                  <Box sx={{
                    width: 80, height: 80, borderRadius: '50%',
                    background: `linear-gradient(135deg,${alpha(f.color, 0.1)},${alpha(f.color, 0.2)})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 2.5,
                  }}>
                    <Box sx={{ color: f.color }}>{f.icon}</Box>
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>{f.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{f.description}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <TrendingUp sx={{ color: f.color, fontSize: 18 }} />
                    <Typography variant="caption" fontWeight="bold" sx={{ color: f.color }}>{f.stats}</Typography>
                  </Box>
                </Card>
              </motion.div>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ══════════════════════ TRENDING CATEGORIES ══════════════════════ */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: 6 }}>
            <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
              Explore Trending
              <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>Fruit Categories</Box>
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Discover the most sought-after fruits in global markets
            </Typography>
          </Box>
          {/* Mobile: horizontal scroll | Desktop: grid */}
          <Box sx={{
            display: { xs: 'flex', sm: 'grid' },
            gridTemplateColumns: { sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
            gap: 2,
            overflowX: { xs: 'auto', sm: 'unset' },
            pb: { xs: 2, sm: 0 },
            scrollSnapType: { xs: 'x mandatory', sm: 'none' },
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#2e7d32', 0.3), borderRadius: 2 },
          }}>
            {trendingCategories.map((cat, i) => (
              <Box key={i} sx={{ minWidth: { xs: 140, sm: 'unset' }, scrollSnapAlign: 'start', flexShrink: 0 }}>
                <motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}>
                  <Card component={Link} to={`/fruits?search=${cat.name}`} sx={{
                    textAlign: 'center', p: 2, borderRadius: 4, textDecoration: 'none',
                    border: `2px solid ${alpha(cat.color, 0.1)}`, transition: 'all 0.3s',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    '&:hover': { bgcolor: alpha(cat.color, 0.05), transform: 'translateY(-4px)' },
                  }}>
                    <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>{cat.icon}</Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>{cat.name}</Typography>
                    <Chip label={`${cat.count} Varieties`} size="small"
                      sx={{ bgcolor: alpha(cat.color, 0.1), color: cat.color, fontWeight: 'bold', fontSize: '0.7rem' }} />
                  </Card>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ══════════════════════ FEATURED FRUITS ══════════════════════ */}
      <Container maxWidth="xl" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 6, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
              Premium Quality
              <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>Fresh from Farms</Box>
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Handpicked quality fruits from verified growers worldwide
            </Typography>
          </Box>
          <Button component={Link} to="/fruits" variant="outlined" size="large"
            endIcon={<ArrowRightAlt />} sx={{ borderRadius: 2 }}>
            View All Fruits
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={56} thickness={4} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>Loading fresh fruits...</Typography>
          </Box>
        ) : fruits.length > 0 ? (
          /* Mobile: horizontal scroll | Desktop: grid */
          <Box sx={{
            display: { xs: 'flex', sm: 'grid' },
            gridTemplateColumns: { sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 3,
            overflowX: { xs: 'auto', sm: 'unset' },
            pb: { xs: 2, sm: 0 },
            scrollSnapType: { xs: 'x mandatory', sm: 'none' },
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#2e7d32', 0.3), borderRadius: 2 },
          }}>
            {fruits.map((fruit, i) => (
              <Box key={fruit.id || i} sx={{ minWidth: { xs: 250, sm: 'unset' }, scrollSnapAlign: 'start', flexShrink: 0 }}>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}>
                  <FruitCard fruit={fruit} />
                </motion.div>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontSize: '3rem', mb: 2 }}>🍃</Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>No fruits available at the moment</Typography>
            <Button variant="outlined" onClick={fetchFruits} sx={{ mt: 1 }}>Retry</Button>
          </Box>
        )}
      </Container>

      {/* ══════════════════════ HOW IT WORKS ══════════════════════ */}
      <Box ref={stepsRef} sx={{ background: 'linear-gradient(135deg,#0c2b0f,#1a5c1f)', py: { xs: 8, md: 15 } }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="START TRADING" sx={{ bgcolor: alpha('#ffeb3b', 0.2), color: '#ffeb3b', fontWeight: 700, mb: 3 }} />
            <Typography sx={{
              fontWeight: 800, color: 'white',
              fontSize: { xs: '2.5rem', md: '4rem' }, mb: 3, lineHeight: 1.1,
            }}>
              Start Trading in{' '}
              <Box component="span" sx={{
                background: 'linear-gradient(90deg,#ffeb3b,#ff9800)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                6 Simple Steps
              </Box>
            </Typography>
          </Box>
          {/* Mobile: horizontal scroll | Desktop: grid */}
          <Box sx={{
            display: { xs: 'flex', sm: 'grid' },
            gridTemplateColumns: { sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            overflowX: { xs: 'auto', sm: 'unset' },
            pb: { xs: 2, sm: 0 },
            scrollSnapType: { xs: 'x mandatory', sm: 'none' },
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#ffeb3b', 0.4), borderRadius: 2 },
          }}>
            {tradingSteps.map((step, i) => (
              <Box key={i} sx={{ minWidth: { xs: 240, sm: 'unset' }, scrollSnapAlign: 'start', flexShrink: 0 }}>
                <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}>
                  <Card sx={{
                    p: 4, borderRadius: 4, bgcolor: alpha('#fff', 0.95),
                    border: `2px solid ${alpha(step.color, 0.2)}`, transition: 'all 0.3s', height: '100%',
                    '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 20px 40px ${alpha(step.color, 0.2)}` },
                  }}>
                    <Box sx={{
                      width: 60, height: 60, borderRadius: '50%',
                      background: `linear-gradient(135deg,${step.color},${alpha(step.color, 0.7)})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mx: 'auto', mb: 3, color: 'white', fontWeight: 900, fontSize: '1.1rem',
                      boxShadow: `0 8px 20px ${alpha(step.color, 0.4)}`,
                    }}>
                      {step.number}
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1.5 }}>{step.title}</Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>{step.description}</Typography>
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        px: 2, py: 0.75, borderRadius: 20, bgcolor: alpha(step.color, 0.1),
                      }}>
                        <AccessTime sx={{ fontSize: 16, color: step.color }} />
                        <Typography variant="caption" fontWeight="bold" sx={{ color: step.color }}>{step.duration}</Typography>
                      </Box>
                    </Box>
                  </Card>
                </motion.div>
              </Box>
            ))}
          </Box>
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Button component={Link} to="/register" variant="contained" size="large" startIcon={<RocketLaunch />}
              sx={{
                background: 'linear-gradient(90deg,#ffeb3b,#ff9800)',
                color: '#1b5e20', fontWeight: 800, borderRadius: 3,
                px: 8, py: 2, fontSize: '1.2rem',
                boxShadow: '0 20px 50px rgba(255,235,59,0.4)',
              }}>
              Start Your Trading Journey
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ══════════════════════ TESTIMONIALS ══════════════════════ */}
      <Container maxWidth="xl" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip label="TESTIMONIALS" sx={{ bgcolor: alpha('#ff9800', 0.1), color: '#ff9800', fontWeight: 600, mb: 2 }} />
          <Typography variant="h3" fontWeight="bold">
            Trusted by <Box component="span" sx={{ color: '#ff9800' }}>Global Traders</Box>
          </Typography>
        </Box>
        {/* Mobile: horizontal scroll | Desktop: grid */}
        <Box sx={{
          display: { xs: 'flex', md: 'grid' },
          gridTemplateColumns: { md: 'repeat(3, 1fr)' },
          gap: 3,
          overflowX: { xs: 'auto', md: 'unset' },
          pb: { xs: 2, md: 0 },
          scrollSnapType: { xs: 'x mandatory', md: 'none' },
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#ff9800', 0.3), borderRadius: 2 },
        }}>
          {testimonials.map((t, i) => (
            <Box key={i} sx={{ minWidth: { xs: 280, md: 'unset' }, scrollSnapAlign: 'start', flexShrink: 0 }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}>
                <Card sx={{ p: 3, borderRadius: 4, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#ff9800', mr: 2 }}>{t.avatar}</Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">{t.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{t.role}</Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex' }}>
                      {[...Array(t.rating)].map((_, j) => (
                        <Star key={j} sx={{ color: '#ff9800', fontSize: 16 }} />
                      ))}
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ fontStyle: 'italic' }}>"{t.content}"</Typography>
                </Card>
              </motion.div>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ══════════════════════ CTA ══════════════════════ */}
      <Container maxWidth="xl" sx={{ mb: { xs: 8, md: 12 } }}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
          <Paper sx={{
            background: 'linear-gradient(135deg,#1b5e20,#2e7d32)',
            color: 'white', p: { xs: 5, md: 8 }, borderRadius: 4, textAlign: 'center',
          }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Ready to Transform Your{' '}
              <Box component="span" sx={{ color: '#ffeb3b' }}>Fruit Business?</Box>
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, maxWidth: 700, mx: 'auto' }}>
              Join thousands of successful traders who trust Smartwave International
              for secure, efficient, and profitable fruit trading.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button component={Link} to="/register" variant="contained" size="large"
                startIcon={<RocketLaunch />}
                sx={{ bgcolor: '#ffeb3b', color: '#1b5e20', fontWeight: 'bold', borderRadius: 3, px: 5 }}>
                Start Free Trial
              </Button>
              <Button component={Link} to="/how-it-works" variant="outlined" size="large"
                startIcon={<PlayArrow />}
                sx={{ borderColor: alpha('#fff', 0.4), borderWidth: 2, color: 'white', borderRadius: 3, px: 5 }}>
                Learn How It Works
              </Button>
            </Stack>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Home;